import React, { createContext, useContext, useState, useEffect } from "react";
import { currencyData } from "../components-v2/constants/currencies";

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

// Re-export for backward compatibility
export const currencies = currencyData;

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Load from localStorage or default to INR
    const saved = localStorage.getItem("selectedCurrency");
    if (saved) return JSON.parse(saved);
    const inr = currencies.find(c => c.code === "INR");
    return inr || currencies[0];
  });

  useEffect(() => {
    // Save to localStorage whenever currency changes
    localStorage.setItem("selectedCurrency", JSON.stringify(currency));
  }, [currency]);

  const convertAmount = (amount, fromCurrency = "INR") => {
    if (!amount) return 0;

    // Convert to base currency (INR) first
    const baseAmount = fromCurrency === "INR"
      ? amount
      : amount / currencies.find(c => c.code === fromCurrency)?.exchangeRate || 1;

    // Then convert to target currency
    return baseAmount * currency.exchangeRate;
  };

  const formatAmount = (amount, showSymbol = true) => {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

    return showSymbol ? `${currency.symbol} ${formatted}` : formatted;
  };

  const calculateTax = (amount) => {
    return (amount * currency.taxRate) / 100;
  };

  const calculateTotal = (amount) => {
    return amount + calculateTax(amount);
  };

  const value = {
    currency,
    setCurrency,
    currencies,
    convertAmount,
    formatAmount,
    calculateTax,
    calculateTotal,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
